<script setup lang="ts">
import { computed } from 'vue';
import { UiText, UiButton, UiIcon, UiLink, UiSquareButton, UiTooltip, getUrgencyLabel } from '@shopexperts/shared';
import type { LeadAdminResponse } from '../types';
import { formatTimezoneWithOffset } from '../utils/timezone';

interface LeadCardProps {
    lead: LeadAdminResponse;
}

const props = defineProps<LeadCardProps>();

const emit = defineEmits<{
    (e: 'assign', lead: LeadAdminResponse): void;
    (e: 'edit', lead: LeadAdminResponse): void;
    (e: 'delete', lead: LeadAdminResponse): void;
    (e: 'mark-resolved', lead: LeadAdminResponse): void;
}>();

// Format date
const formattedDate = computed(() => {
    const date = new Date(props.lead.created_at);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
});

// Status badge color
const statusColor = computed(() => {
    return props.lead.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800';
});

// Urgency badge color
const urgencyColor = computed(() => {
    switch (props.lead.urgency) {
        case 'urgent':
        case 'as_soon_as_possible':
            return 'bg-red-100 text-red-800';
        case 'within_the_next_week':
            return 'bg-orange-100 text-orange-800';
        case 'within_the_next_month':
            return 'bg-yellow-100 text-yellow-800';
        case 'standard':
        case 'open_to_expert_opinion':
        default:
            return 'bg-blue-100 text-blue-800';
    }
});

// Urgency display label
const urgencyLabel = computed(() => getUrgencyLabel(props.lead.urgency));

const timezoneLabel = computed(() => formatTimezoneWithOffset(props.lead.timezone));

// Show mark as resolved only when status is pending
const showMarkResolved = computed(() => {
    return props.lead.status === 'pending';
});

// Can mark as resolved only if there's at least one expert assigned
const canMarkResolved = computed(() => {
    return props.lead.matches.length > 0;
});

function onAssignClick() {
    emit('assign', props.lead);
}

function onEditClick() {
    emit('edit', props.lead);
}

function onDeleteClick() {
    emit('delete', props.lead);
}

function onMarkResolvedClick() {
    emit('mark-resolved', props.lead);
}
</script>

<template>
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
        <div class="flex gap-6 items-stretch">
            <div class="flex-1 flex flex-col min-w-0">

                <!-- Project Title & Client -->
                <div class="mb-3">
                    <div class="flex items-center gap-2">
                        <UiText as="h5" variant="h5" class="font-semibold mb-1 text-se-gray-500">#{{ lead.id }}</UiText>
                        <UiText as="h4" variant="p" class="font-semibold mb-1">
                            {{ lead.project_title }}
                        </UiText>
                    </div>
                    <UiLink :href="lead.store.url" external="with-arrow" variant="p-small"
                        :label="lead.store.title + ' - ' + lead.store.url" />
                </div>

                <!-- Project Brief (if exists) -->
                <UiText v-if="lead.project_brief" as="p" variant="p-small" class="text-gray-600 mb-3"
                    :text="lead.project_brief" :truncate="3" :show-more-button="true" />

                <div class="flex-1"></div>

                <!-- Metadata Row -->
                <div class="grid grid-cols-2 md:grid-cols-4 items-start text-xs text-gray-600 gap-2 justify-space-between w-full">
                    <div>
                        <UiText as="caption" variant="caption-normal" class="text-gray-600" text="Budget: " />
                        <UiText as="caption" variant="caption-normal" class="font-semibold" :text="lead.budget_range" />
                    </div>
                    <div>
                        <UiText as="caption" variant="caption-normal" class="text-gray-600" text="Timezone: " />
                        <UiText as="caption" variant="caption-normal" class="font-semibold" :text="timezoneLabel" />
                    </div>
                    <div>
                        <UiText as="caption" variant="caption-normal" class="text-gray-600" text="Created: " />
                        <UiText as="caption" variant="caption-normal" class="font-semibold" :text="formattedDate" />
                    </div>
                    <div>
                        <UiText as="caption" variant="caption-normal" class="text-gray-600"
                            text="Matching Strategy: " />
                        <UiText as="caption" variant="caption-normal" class="font-semibold capitalize"
                            :text="lead.matching_strategy" />
                    </div>
                </div>
            </div>

            <!-- Right Column: Experts & Actions -->
            <div class="w-80 flex-shrink-0 flex flex-col gap-2">
                <div class="flex gap-1 justify-end">
                    <span :class="urgencyColor" class="px-2 py-0.5 rounded text-xs font-medium">
                        {{ urgencyLabel }}
                    </span>
                    <span :class="statusColor" class="px-2 py-0.5 rounded text-xs font-medium capitalize">
                        {{ lead.status }}
                    </span>
                </div>
                <!-- Expert Progress -->
                <div class="mb-3 p-2 bg-gray-50 rounded-md">
                    <div class="flex items-center justify-between mb-1">
                        <UiText as="p" variant="p-small" class="text-gray-700 text-xs">
                            Experts Assigned
                        </UiText>
                        <UiText as="p" variant="p-small" class="font-bold text-xs">
                            {{ lead.matches_count }}/{{ lead.max_matches }}
                        </UiText>
                    </div>
                    <div class="w-full bg-gray-200 rounded-full h-1.5">
                        <div class="bg-primary h-1.5 rounded-full transition-all"
                            :style="{ width: `${(lead.matches_count / lead.max_matches) * 100}%` }"></div>
                    </div>
                </div>

                <!-- Selected Expert (if any) -->
                <div v-if="lead.selected_expert" class="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-md">
                    <UiText as="p" variant="p-small" class="text-blue-800 text-xs mb-1">
                        Selected Expert
                    </UiText>
                    <div class="flex items-center gap-2">
                        <div v-if="lead.selected_expert.avatar_media"
                            class="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                            <img :src="lead.selected_expert.avatar_media.cdn_url" :alt="lead.selected_expert.name"
                                class="w-full h-full object-cover" />
                        </div>
                        <div class="flex-1 min-w-0">
                            <UiText as="p" variant="p-small" class="font-semibold text-xs truncate">
                                {{ lead.selected_expert.name }}
                            </UiText>
                        </div>
                    </div>
                </div>

                <!-- Matched Experts Compact List -->
                <div v-if="lead.matches.length > 0" class="mb-3 flex-1 min-h-0">
                    <UiText as="p" variant="p-small" class="text-gray-600 text-xs mb-1">
                        Matched ({{ lead.matches.length }})
                    </UiText>
                    <div class="space-y-1 max-h-24 overflow-y-auto">
                        <div v-for="match in lead.matches" :key="match.id"
                            class="flex items-center gap-2 p-1.5 bg-gray-50 rounded text-xs">
                            <div v-if="match.expert.avatar_media"
                                class="w-5 h-5 rounded-full overflow-hidden flex-shrink-0">
                                <img :src="match.expert.avatar_media.cdn_url" :alt="match.expert.name"
                                    class="w-full h-full object-cover" />
                            </div>
                            <div class="flex-1 min-w-0 truncate">
                                <span class="font-semibold">{{ match.expert.name }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Action Button -->
                <div class="mt-auto">
                    <div class="flex items-center gap-2" v-if="lead.can_match_more">
                        <UiButton variant="primary" label="Assign Experts" class="w-full" size="md"
                            @click="onAssignClick">
                            <template #before_label>
                                <UiIcon name="user-circle" class="w-4 h-4" />
                            </template>
                        </UiButton>
                        <UiTooltip content="Edit lead">
                            <UiSquareButton icon="pencil" variant="ghost" @click="onEditClick" />
                        </UiTooltip>
                        <UiTooltip v-if="showMarkResolved"
                            :content="canMarkResolved ? 'Mark resolved' : 'Assign experts first'">
                            <UiSquareButton icon="check-circle" variant="ghost" :disabled="!canMarkResolved"
                                @click="onMarkResolvedClick" />
                        </UiTooltip>
                        <UiTooltip content="Delete lead">
                            <UiSquareButton icon="delete" variant="ghost" @click="onDeleteClick" />
                        </UiTooltip>
                    </div>
                    <div v-else class="flex items-center gap-2">
                        <UiText as="p" variant="p-small" class="text-center text-gray-500 italic text-xs flex-1">
                            Maximum experts assigned
                        </UiText>
                        <UiTooltip content="Edit lead">
                            <UiSquareButton icon="pencil" variant="ghost" @click="onEditClick" />
                        </UiTooltip>
                        <UiTooltip v-if="showMarkResolved"
                            :content="canMarkResolved ? 'Mark resolved' : 'Assign experts first'">
                            <UiSquareButton icon="check-circle" variant="ghost" :disabled="!canMarkResolved"
                                @click="onMarkResolvedClick" />
                        </UiTooltip>
                        <UiTooltip content="Delete lead">
                            <UiSquareButton icon="delete" variant="ghost" @click="onDeleteClick" />
                        </UiTooltip>
                    </div>
                </div>
            </div>
        </div>

        <!-- Divider -->
        <div class="border-t border-gray-200 mt-4 pt-4">
            <!-- Client Details -->
            <div class="flex items-center gap-4">
                <UiText as="p" variant="p-small" class="text-gray-500 text-xs">
                    Client:
                </UiText>
                <UiText as="p" variant="p-small" class="font-semibold text-sm">
                    {{ lead.client.owner_name || lead.client.name }}
                </UiText>
                <UiLink 
                    v-if="lead.client.owner_email"
                    :href="`mailto:${lead.client.owner_email}`" 
                    variant="p-small"
                    :label="lead.client.owner_email" 
                    class="text-blue-600 hover:underline"
                />
            </div>
        </div>
    </div>
</template>
